import { Injectable } from '@angular/core';

/**
 * App-wide popup for server errors (5xx), shown on top of every page — including
 * open Material dialogs (cdk overlay, z-index 1000) and the app's own 10000 layers.
 *
 * Many screens show a fixed text on failure ("Could not reach the blockchain") or
 * only log to the console, so the user never sees the reference id the API returns
 * (security finding M7). LoggingInterceptor calls show() for every 5xx, so the id is
 * always visible and copyable, whatever the screen does with the error.
 *
 * Plain DOM appended to <body> rather than a MatSnackBar: a snackbar opened by the
 * component would replace it, and it needs no module declarations. Text is set with
 * textContent only (never innerHTML), so a server message cannot inject markup.
 *
 * One popup at a time: errors arriving while it is open update it to the latest
 * reference and count the earlier ones.
 */
export const ERROR_POPUP_Z_INDEX = 2147483647; // the maximum z-index browsers allow
const AUTO_CLOSE_MS = 30000;

@Injectable({ providedIn: 'root' })
export class ErrorPopupService {
    private host: HTMLElement | null = null;
    private messageEl!: HTMLElement;
    private referenceEl!: HTMLElement;
    private countEl!: HTMLElement;
    private copyBtn!: HTMLButtonElement;
    private reference = '';
    private count = 0;
    private timer: any = null;

    show(message: string, reference: string): void {
        if (!this.host) this.create();
        this.count++;
        this.reference = reference;
        this.messageEl.textContent = message;
        this.referenceEl.textContent = reference;
        this.countEl.textContent = this.count > 1 ? `${this.count - 1} earlier error(s) also occurred.` : '';
        this.countEl.style.display = this.count > 1 ? 'block' : 'none';
        this.copyBtn.textContent = 'Copy';
        this.restartTimer();
    }

    close(): void {
        clearTimeout(this.timer);
        this.timer = null;
        this.host?.remove();
        this.host = null;
        this.count = 0;
    }

    private restartTimer(): void {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), AUTO_CLOSE_MS);
    }

    private create(): void {
        const el = (tag: string, style: string, text = '') => {
            const node = document.createElement(tag);
            node.setAttribute('style', style);
            node.textContent = text;
            return node;
        };

        const host = el('div', [
            'position:fixed', 'top:16px', 'left:50%', 'transform:translateX(-50%)',
            `z-index:${ERROR_POPUP_Z_INDEX}`, 'width:min(520px, calc(100vw - 32px))', 'box-sizing:border-box',
            'background:#fff', 'border:1px solid #f5c2c0', 'border-left:5px solid #c62828', 'border-radius:10px',
            'box-shadow:0 10px 30px rgba(0,0,0,.25)', 'padding:14px 16px', 'font-family:inherit',
            'color:#3b1514', 'font-size:14px', 'line-height:1.45'
        ].join(';'));
        host.setAttribute('role', 'alert');
        host.setAttribute('aria-live', 'assertive');
        host.classList.add('app-error-popup');
        // Hovering pauses the auto-close so the user can read/copy the id.
        host.addEventListener('mouseenter', () => clearTimeout(this.timer));
        host.addEventListener('mouseleave', () => this.restartTimer());

        const header = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:6px');
        header.appendChild(el('strong', 'color:#c62828;font-size:15px', 'Something went wrong'));
        const closeBtn = el('button', 'border:none;background:none;font-size:20px;line-height:1;cursor:pointer;color:#7a2e2a;padding:0 4px', '×') as HTMLButtonElement;
        closeBtn.type = 'button';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('click', () => this.close());
        header.appendChild(closeBtn);

        this.messageEl = el('div', 'margin-bottom:10px');

        const refRow = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;background:#fdf1f0;border-radius:6px;padding:6px 8px');
        refRow.appendChild(el('span', 'font-weight:600', 'Reference ID:'));
        this.referenceEl = el('code', 'font-family:Consolas,monospace;font-size:13px;word-break:break-all;flex:1;user-select:all');
        refRow.appendChild(this.referenceEl);
        this.copyBtn = el('button', 'border:1px solid #c62828;background:#fff;color:#c62828;border-radius:5px;padding:2px 10px;cursor:pointer;font-size:12px') as HTMLButtonElement;
        this.copyBtn.type = 'button';
        this.copyBtn.addEventListener('click', () => this.copy());
        refRow.appendChild(this.copyBtn);

        this.countEl = el('div', 'margin-top:6px;font-size:12px;color:#7a5250');

        host.append(header, this.messageEl, refRow, this.countEl);
        document.body.appendChild(host);
        this.host = host;
    }

    private copy(): void {
        const done = () => (this.copyBtn.textContent = 'Copied');
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(this.reference).then(done, () => this.selectReference());
        } else {
            this.selectReference();
        }
    }

    /** Clipboard API unavailable (non-HTTPS host): select the id for Ctrl+C. */
    private selectReference(): void {
        const range = document.createRange();
        range.selectNodeContents(this.referenceEl);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        this.copyBtn.textContent = 'Press Ctrl+C';
    }
}
